import { supabase } from '../client';

/**
 * Per-store template overrides.
 *
 * Backed by `core.store_email_template_overrides` /
 * `core.store_sms_template_overrides` and the four SECURITY DEFINER RPCs
 * shipped in `supabase/migrations/20260612060200_store_template_overrides_rpcs.sql`:
 *
 *   - core.rpc_list_store_template_catalog(p_store_id)
 *   - core.rpc_upsert_store_email_template_override(...)
 *   - core.rpc_upsert_store_sms_template_override(...)
 *   - core.rpc_reset_store_template_override(...)
 *
 * The list RPC returns one row per overridable template (email + SMS),
 * with the platform default and the per-store override merged into a
 * single shape for the editor.
 */

export type StoreTemplateChannel = 'email' | 'sms';

export interface StoreTemplateCatalogEntry {
  channel: StoreTemplateChannel;
  key: string;
  name: string | null;
  description: string | null;
  category: string | null;
  compliance_class: string | null;
  variables: unknown;
  sample_data: unknown;
  platform_subject: string | null;
  platform_body_html: string | null;
  platform_body_text: string | null;
  platform_from_address: string | null;
  platform_reply_to: string | null;
  platform_send_style: string | null;
  platform_media_url: string | null;
  override_subject: string | null;
  override_body_html: string | null;
  override_body_text: string | null;
  override_from_address: string | null;
  override_reply_to: string | null;
  override_body: string | null;
  override_send_style: string | null;
  override_media_url: string | null;
  override_enabled: boolean;
  has_override: boolean;
  updated_at: string | null;
}

export async function listStoreTemplateCatalog(
  storeId: string,
): Promise<StoreTemplateCatalogEntry[]> {
  // The generated `Database` type isn't yet refreshed for these new
  // RPCs, so narrow to `any` at the schema() callsite.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data, error } = await (supabase() as any)
    .schema('core')
    .rpc('rpc_list_store_template_catalog', { p_store_id: storeId });
  if (error) throw error;
  return (data ?? []) as StoreTemplateCatalogEntry[];
}

export interface UpsertStoreEmailTemplateOverrideInput {
  storeId: string;
  templateKey: string;
  subject?: string | null;
  bodyHtml?: string | null;
  bodyText?: string | null;
  fromAddress?: string | null;
  replyTo?: string | null;
  enabled?: boolean | null;
}

export async function upsertStoreEmailTemplateOverride(
  input: UpsertStoreEmailTemplateOverrideInput,
): Promise<void> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { error } = await (supabase() as any)
    .schema('core')
    .rpc('rpc_upsert_store_email_template_override', {
      p_store_id: input.storeId,
      p_template_key: input.templateKey,
      p_subject: input.subject ?? null,
      p_body_html: input.bodyHtml ?? null,
      p_body_text: input.bodyText ?? null,
      p_from_address: input.fromAddress ?? null,
      p_reply_to: input.replyTo ?? null,
      p_enabled: input.enabled ?? null,
    });
  if (error) throw error;
}

export interface UpsertStoreSmsTemplateOverrideInput {
  storeId: string;
  templateKey: string;
  body?: string | null;
  sendStyle?: string | null;
  mediaUrl?: string | null;
  enabled?: boolean | null;
}

export async function upsertStoreSmsTemplateOverride(
  input: UpsertStoreSmsTemplateOverrideInput,
): Promise<void> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { error } = await (supabase() as any)
    .schema('core')
    .rpc('rpc_upsert_store_sms_template_override', {
      p_store_id: input.storeId,
      p_template_key: input.templateKey,
      p_body: input.body ?? null,
      p_send_style: input.sendStyle ?? null,
      p_media_url: input.mediaUrl ?? null,
      p_enabled: input.enabled ?? null,
    });
  if (error) throw error;
}

export async function resetStoreTemplateOverride(
  storeId: string,
  templateKey: string,
  channel: StoreTemplateChannel,
): Promise<void> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { error } = await (supabase() as any)
    .schema('core')
    .rpc('rpc_reset_store_template_override', {
      p_store_id: storeId,
      p_template_key: templateKey,
      p_channel: channel,
    });
  if (error) throw error;
}
